import React from "react";

/** Named accent → value text class (overridable via ``valueClassName``). */
export const METRIC_TILE_ACCENTS = {
  overtime: "text-amber-600",
  doubletime: "text-yellow-600",
};

/**
 * Resolve the value text class for a metric tile.
 *
 * @param {string} [accent] - Named accent key (e.g. ``overtime``).
 * @param {string} [valueClassName] - Explicit value class override.
 * @returns {string}
 */
function resolveValueClassName(accent, valueClassName) {
  if (valueClassName) return valueClassName;
  if (accent && METRIC_TILE_ACCENTS[accent]) return METRIC_TILE_ACCENTS[accent];
  return "text-gray-950";
}

/**
 * Render a single metric tile (value + label).
 *
 * @param {Object} props - Component props.
 * @param {string|number} props.value - Primary metric value.
 * @param {string} props.label - Secondary metric label.
 * @param {string} [props.accent] - Named accent (``overtime``, ``doubletime``).
 * @param {string} [props.valueClassName] - Override for the value text classes.
 * @param {string} [props.labelClassName] - Override for the label text classes.
 * @param {string} [props.className] - Extra classes on the tile container.
 * @returns {JSX.Element}
 */
export const MetricTile = ({
  value,
  label,
  accent,
  valueClassName,
  labelClassName = "",
  className = "",
}) => (
  <div
    className={`rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 shadow-sm ${className}`}
  >
    <div
      className={`text-xl font-bold tracking-tight ${resolveValueClassName(
        accent,
        valueClassName
      )}`}
    >
      {value}
    </div>
    <div
      className={`text-xs font-medium uppercase tracking-wide text-gray-500 ${labelClassName}`}
    >
      {label}
    </div>
  </div>
);

/**
 * Shared row of metric tiles for overview / ticket summary cards.
 *
 * Accepts any count of metrics and keeps them on one row until the container is
 * narrow (``min-w`` on each tile). Per-tile overrides: ``accent``,
 * ``valueClassName``, ``labelClassName``, ``className``.
 *
 * @param {Object} props - Component props.
 * @param {Array<{
 *   value: string|number|React.ReactNode,
 *   label: string,
 *   key?: string,
 *   accent?: string,
 *   valueClassName?: string,
 *   labelClassName?: string,
 *   className?: string,
 *   sizeToContent?: boolean,
 * }>} props.metrics - Metric definitions. ``sizeToContent`` tiles size to their
 *   value width; remaining tiles share leftover space evenly (``flex-1``).
 * @param {string} [props.className] - Extra classes on the row wrapper.
 * @param {string} [props.tileClassName] - Classes applied to every tile.
 * @returns {JSX.Element|null}
 */
const MetricTiles = ({ metrics = [], className = "", tileClassName = "" }) => {
  if (!metrics.length) return null;

  const hasContentSized = metrics.some((metric) => metric.sizeToContent);

  return (
    <div
      className={`flex gap-2 ${hasContentSized ? "flex-nowrap" : "flex-wrap"} ${className}`}
    >
      {metrics.map((metric, index) => {
        let layoutClass = "min-w-[5.5rem] flex-1";
        if (metric.className) {
          layoutClass = metric.className;
        } else if (metric.sizeToContent) {
          // Size to intrinsic value width; siblings with flex-1 share the rest
          layoutClass = "flex-none w-max max-w-full shrink-0";
        } else if (hasContentSized) {
          // Even leftover share when a sibling is content-sized
          layoutClass = "min-w-0 flex-1";
        }

        return (
          <MetricTile
            key={metric.key ?? metric.label ?? index}
            value={metric.value}
            label={metric.label}
            accent={metric.accent}
            valueClassName={metric.valueClassName}
            labelClassName={metric.labelClassName}
            className={`${tileClassName} ${layoutClass}`.trim()}
          />
        );
      })}
    </div>
  );
};

export default MetricTiles;
